import * as React from "react";
import {useId, useState} from "react";
import type {FieldError} from "@/shared/timr";
import {BreakTimeRecordingMode} from "@/shared/timrTypes";
import {FormGroup} from "@/shared/ui/form/FormGroup";
import {DatePickerInput} from "@/shared/ui/primitives/DatePickerInput";
import {TimeInputWithShiftDropdown} from "@/shared/ui/dateTimeRange/TimeInputWithShiftDropdown";
import {TimeZoneInput} from "@/shared/ui/primitives/TimeZoneInput";
import {DurationInput} from "@/shared/ui/primitives/DurationInput";
import {NumberInput} from "@/shared/ui/primitives/NumberInput";
import {getCurrentISOOffset, Temporal} from "@/shared/utils/datefunctions";
import {
	applyBreakTimeChange,
	applyDateChange,
	applyDateTimeChange,
	applyDurationChange,
	applyTimeZoneChange,
	createEditState,
	currentDuration,
	shouldShowTimeZoneFields,
	type DateTimeRangeEditState,
	type DateTimeRangeValue
} from "@/shared/ui/dateTimeRange/dateTimeRangeState";
import {clsx} from "clsx";

interface DateTimeRangePanelProps {
	/** The current date/time range being edited; controlled — the parent holds it and passes back each {@link DateTimeRangePanelProps.onValueChange}. */
	value: DateTimeRangeValue;
	/** Server-side validation errors to surface on the matching fields, keyed by field name (`start`, `startTime`, `breakTime`, …). */
	fieldErrors: FieldError[];
	/** Renders the fields read-only for the current record; orthogonal to the edited data. */
	disabled: boolean;
	/** Called after every user edit with the next committed value */
	onValueChange: (value: DateTimeRangeValue) => void;
	/** The most recent record's end for the start field's "shift to last end" shortcut; omit to hide that shortcut. */
	lastRecordEnd?: Temporal.ZonedDateTime | null;
	/** Whether the break-time field is editable (independent of the date/time fields). */
	breakTimeEditable: boolean;
	/** Reveal the timezone fields when the seeded offsets differ from the current zone; set when duplicating a record. */
	forceTimeZoneFields?: boolean;
	/** Re-anchor a calculated endpoint's offset to its local date instead of the persisted source offset; set when duplicating a record. */
	localizeCalculatedOffset?: boolean;
}

function fieldError(fieldErrors: FieldError[], ...fieldNames: string[]): string | undefined {
	return fieldErrors.find(error => fieldNames.includes(error.field))?.message;
}

function isBreakTimeVisible(value: DateTimeRangeValue): boolean {
	const isBookableBreaksMode = globals.breakTimeRecordingMode === BreakTimeRecordingMode.RECORD_WITH_POSITION;
	return !isBookableBreaksMode || value.breakTime > 0;
}

function isBreakTimeDisabled(value: DateTimeRangeValue, disabled: boolean, breakTimeEditable: boolean): boolean {
	const isBookableBreaksMode = globals.breakTimeRecordingMode === BreakTimeRecordingMode.RECORD_WITH_POSITION;
	// In bookable-breaks mode a recorded break shows read-only; a zero break stays enabled because the
	// hidden field must still submit it.
	return disabled ||
		!breakTimeEditable ||
		!globals.manualBreaksAllowed ||
		(isBookableBreaksMode && value.breakTime > 0);
}

/**
 * One side (start or end) of the range: date picker, time input with the "now"/"last end" shift dropdown,
 * and an optional timezone field. Endpoint-agnostic beyond labeling/ids — the caller resolves the value
 * slice and wraps each callback with whichever `apply*` state transition applies to its endpoint, since
 * those need the full range (to recalculate the opposite endpoint when duration-anchored).
 */
function DateTimeRangeFields({
	endpoint,
	idPrefix,
	value,
	onDateChange,
	onDateTimeChange,
	onTimeZoneChange,
	fieldErrors,
	timeZoneFieldsVisible,
	disabled,
	lastRecordEnd
}: Readonly<{
	endpoint: "start" | "end";
	idPrefix: string;
	value: Temporal.ZonedDateTime | null;
	onDateChange: (value: Temporal.PlainDate | null) => void;
	onDateTimeChange: (value: Temporal.ZonedDateTime | null) => void;
	onTimeZoneChange: (value: string | null) => void;
	fieldErrors: FieldError[];
	timeZoneFieldsVisible: boolean;
	disabled: boolean;
	lastRecordEnd?: Temporal.ZonedDateTime | null;
}>) {
	const isStart = endpoint === "start";

	const dateId = `${idPrefix}${endpoint}Date`;
	const timeId = `${idPrefix}${endpoint}Time`;

	return (
		<FormGroup
			label={messages[isStart ? "workingTime.start" : "workingTime.end"]}
			htmlFor={dateId}
			error={fieldError(fieldErrors, endpoint, `${endpoint}Date`, `${endpoint}Time`, `${endpoint}TimeZone`)}
		>
			<div className="dateTimeRow">
				<div className="row">
					<div className="col-md-12 dateTimeGroup">
						<div className="input-group">
							<div className="input-group-btn">
								<label htmlFor={dateId} className={clsx("btn btn-default btn-icon", disabled && "disabled")}>
									<i className="far fa-calendar-day" />
								</label>
							</div>
							<DatePickerInput
								id={dateId}
								value={value?.toPlainDate() ?? null}
								onValueChange={onDateChange}
								className={`dateField rangeDate`}
								disabled={disabled}
							/>
							<div className="input-group-btn durationUnit-minutes">
								<label htmlFor={timeId} className="btn btn-default btn-icon btn-focus-centered">
									<i className="far fa-clock"/>
								</label>
							</div>
							<TimeInputWithShiftDropdown
								id={timeId}
								value={value}
								onValueChange={onDateTimeChange}
								className={`timeField rangeTime durationUnit-minutes no-border-radius-left${timeZoneFieldsVisible ? "" : " border-radius-right"}`}
								disabled={disabled}
								lastRecordEnd={isStart ? lastRecordEnd : undefined}
							/>
							{timeZoneFieldsVisible && (
								<>
									<span className="input-group-addon durationUnit-minutes timeZoneField"><i className="far fa-globe"/></span>
									<TimeZoneInput
										value={value?.offset ?? getCurrentISOOffset()}
										onValueChange={onTimeZoneChange}
										className="durationUnit-minutes timeZoneField"
										disabled={disabled}
									/>
								</>
							)}
						</div>
					</div>
				</div>
			</div>
		</FormGroup>
	);
}

/** The three per-endpoint edits, each needing the full range to recalculate the opposite endpoint when duration-anchored. */
function endpointCallbacks(value: DateTimeRangeEditState, endpoint: "start" | "end", localizeCalculatedOffset: boolean, commit: (next: DateTimeRangeEditState) => void) {
	return {
		onDateChange: (date: Temporal.PlainDate | null) => commit(applyDateChange(value, endpoint, date, localizeCalculatedOffset)),
		onDateTimeChange: (dateTime: Temporal.ZonedDateTime | null) => commit(applyDateTimeChange(value, endpoint, dateTime, localizeCalculatedOffset)),
		onTimeZoneChange: (timeZone: string | null) => commit(applyTimeZoneChange(value, endpoint, timeZone, localizeCalculatedOffset))
	};
}

export function DateTimeRangePanel({
	value: seededValue,
	fieldErrors,
	disabled,
	onValueChange,
	lastRecordEnd,
	breakTimeEditable,
	forceTimeZoneFields = false,
	localizeCalculatedOffset = false
}: Readonly<DateTimeRangePanelProps>) {
	const idPrefix = useId();

	// The panel owns the working value: the parent seeds it and hears about edits via onValueChange, but
	// does not re-feed on every keystroke — so anchoring and the derived duration are reflected from local
	// state here. A NEW seededValue object is the reseed signal (dialog open, server errors); comparing
	// during render instead of in an effect avoids committing a frame with the stale previous value.
	const [value, setValue] = useState<DateTimeRangeEditState>(() => createEditState(seededValue));
	const [lastSeededValue, setLastSeededValue] = useState(seededValue);
	if (lastSeededValue !== seededValue) {
		setLastSeededValue(seededValue);
		setValue(createEditState(seededValue));
	}

	const commit = (next: DateTimeRangeEditState) => {
		setValue(next);
		onValueChange(next);
	};

	const timeZoneFieldsVisible = shouldShowTimeZoneFields(value, forceTimeZoneFields);
	const duration = currentDuration(value);
	const breakTimeVisible = isBreakTimeVisible(value);
	const breakTimeDisabled = isBreakTimeDisabled(value, disabled, breakTimeEditable);

	const endpointProps = {idPrefix, fieldErrors, timeZoneFieldsVisible, disabled};
	const breakTimeId = `${idPrefix}breakTime`;
	const durationId = `${idPrefix}duration`;
	const breakTimeError = fieldError(fieldErrors, "breakTime");
	const commitDuration = (nextDuration: number) => commit(applyDurationChange(value, nextDuration, localizeCalculatedOffset));

	return (
		<div className="date-time-range-panel">
			<DateTimeRangeFields
				endpoint="start"
				{...endpointProps}
				value={value.start}
				{...endpointCallbacks(value, "start", localizeCalculatedOffset, commit)}
				lastRecordEnd={lastRecordEnd}
			/>
			<DateTimeRangeFields
				endpoint="end"
				{...endpointProps}
				value={value.end}
				{...endpointCallbacks(value, "end", localizeCalculatedOffset, commit)}
			/>

			<div className="form-group durationUnit-minutes">
				<div className="col-xs-2">
					<label
						className="control-label pt-8 w-100"
						htmlFor={breakTimeId}
						style={{display: breakTimeVisible ? undefined : "none"}}
					>
						{messages["workingTime.breakTime"]}
					</label>
				</div>
				<div className="col-xs-4">
					<div className={`input-group${breakTimeError ? " has-error" : ""}`} style={{display: breakTimeVisible ? undefined : "none"}}>
						<NumberInput
							id={breakTimeId}
							className="breakTime"
							maxLength={5}
							value={value.breakTime}
							onValueChange={breakTime => commit(applyBreakTimeChange(value, breakTime, localizeCalculatedOffset))}
							disabled={breakTimeDisabled}
						/>
						<span className="input-group-addon">{messages["workingTime.breakTime.minutes"]}</span>
					</div>
					{breakTimeError && <span className="help-block">{breakTimeError}</span>}
				</div>
				<label className="control-label col-xs-2" htmlFor={durationId}>{messages["record.duration"]}</label>
				<div className="col-xs-4">
					<div className="input-group">
						<DurationInput
							id={durationId}
							value={duration}
							onValueChange={commitDuration}
							className="duration"
							disabled={disabled}
						/>
						<span className="input-group-addon">{messages["timeFormat.addon." + globals.durationFormat]}</span>
					</div>
				</div>
			</div>
		</div>
	);
}

export type {DateTimeRangePanelProps};
